import { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

export const BeforeAfter = ({ before, after, beforeAlt = "Before", afterAlt = "After", caption }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const update = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onDown = (e) => {
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    update(e.clientX);
  };
  const onMove = (e) => { if (dragging) update(e.clientX); };
  const stop = () => setDragging(false);
  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
  };

  return (
    <figure className="select-none">
      <div
        ref={ref}
        data-testid="before-after-slider"
        role="slider"
        tabIndex={0}
        aria-label={caption ? `${caption} before and after` : "Before and after"}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        className="relative w-full aspect-[3/4] overflow-hidden cursor-ew-resize touch-none bg-muted focus:outline-none focus:ring-2 focus:ring-blush"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={stop}
        onKeyDown={onKey}
      >
        {/* After (base) */}
        <img src={after} alt={afterAlt} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
        {/* Before (clipped) */}
        <img
          src={before}
          alt={beforeAlt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        />

        {/* Labels */}
        <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-espresso/80 text-cream px-3 py-1 backdrop-blur-sm">Before</span>
        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] bg-blush text-white px-3 py-1">After</span>

        {/* Divider + handle */}
        <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
          <div className="w-0.5 h-full bg-cream shadow-[0_0_10px_rgba(0,0,0,0.3)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-cream text-espresso flex items-center justify-center shadow-lg">
            <MoveHorizontal size={20} />
          </div>
        </div>
      </div>
      {caption && (
        <figcaption className="mt-4 flex items-center justify-between">
          <span className="font-serif italic text-xl text-espresso">{caption}</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-cocoa">Drag to reveal</span>
        </figcaption>
      )}
    </figure>
  );
};
